import React, { FormEvent, useEffect, useState } from 'react';
import cc from 'classcat';
import Dialog from '../dialog';
import Modal from '../modal';
import Tooltip from '../tooltip';
import { SimpleSelect } from '../select';
import Icon from '../../atoms/icon';
import Text from '../../atoms/text';
import {
  faBoxOpen,
  faBox,
  faQuestionCircle
} from '@fortawesome/pro-solid-svg-icons';
import AnchorButton from '../../molecules/button/anchor';
import Button from '../../molecules/button';
import {
  GetFullName,
  IPatient,
  SavePatientResponse
} from '../../../libs/patient';
import { useTriageUpdater } from '../../../contexts/triage/updater';
import { PostJSON } from '../../../libs/request';
import { useMetaData } from '../../../contexts/metadata';
import { Classes, Label, RadioGroup, TextArea } from '@blueprintjs/core';

/**
 * This action kicks off the
 * patient archive flow
 */

interface ArchivePatientActionProps {
  patient: IPatient;
  bare?: boolean;
  className?: string;
  fill?: boolean;
  compact?: boolean;
}

const isConfirmDisabled = (isArchived: boolean, hasArchiveReason: boolean) => {
  if (isArchived) {
    return false;
  }
  if (hasArchiveReason) {
    return false;
  }
  return true;
};

export const ArchivePatientAction = ({
  patient,
  className,
  bare = false,
  fill = false,
  compact = false
}: ArchivePatientActionProps): JSX.Element => {
  const { terms } = useMetaData();
  const { settings } = useMetaData();
  const { handleToggleArchivePatient } = useTriageUpdater();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [archiveComment, setArchiveComment] = useState('');
  const icon = patient.isArchived ? faBoxOpen : faBox;
  const title = patient.isArchived
    ? `Unarchive ${terms.patient.capitalized}`
    : `Archive ${terms.patient.capitalized}`;
  const classNames = [className, 'justify-left'];
  const archiveOptions =
    settings && settings.archiveOptions && settings.archiveOptions.length
      ? settings.archiveOptions
      : null;
  const fullName = GetFullName(patient);
  const dialogTitle = patient.isArchived
    ? `Unarchive ${fullName}`
    : `Archive ${fullName}`;
  const numArchiveOptions = archiveOptions ? archiveOptions.length : 0;

  useEffect(() => {
    if (isDialogOpen) {
      let reason = '';
      if (archiveOptions?.length > 0) {
        reason = archiveOptions[0].value;
      }
      setArchiveReason(reason);
    }
  }, [isDialogOpen]);

  const handleReasonSelection = (
    event: React.ChangeEvent<HTMLSelectElement> | FormEvent<HTMLInputElement>
  ) => {
    let reason = '';
    if (event.currentTarget && event.currentTarget.value) {
      reason = event.currentTarget.value;
    }
    if (event.target && 'value' in event.target) {
      reason = event.target.value;
    }
    setArchiveReason(reason);
  };

  const handleCommentChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setArchiveComment(event.target.value);
  };

  const handleClose = () => {
    setArchiveReason('');
    setArchiveComment('');
    setIsDialogOpen(false);
  };

  const handleConfirm = () => {
    PostJSON<SavePatientResponse>({
      url: '/patient/edit/',
      params: {
        id: patient.id,
        isArchived: !patient.isArchived,
        archiveReason,
        archiveComment: archiveReason === 'other' ? archiveComment : undefined
      }
    }).then((response) => {
      if (response.status !== 'OK') {
        Modal.error({
          title: patient.isArchived ? 'Error Unarchiving' : 'Error Archiving',
          content: response.message
        });
        return;
      }
      const updatedPatient = (response as SavePatientResponse).patient;
      handleToggleArchivePatient(updatedPatient);
      handleClose();
    });
  };

  return (
    <React.Fragment>
      <Dialog
        title={dialogTitle}
        icon={<Icon title={false} icon={faQuestionCircle} className="mr-2" />}
        isOpen={isDialogOpen}
        onClose={handleClose}
        maxWidth="650px"
        style={{
          // Mimics the background for Modal.confirm
          background: '#F2EFE8'
        }}
      >
        <div className={Classes.DIALOG_BODY}>
          {patient.isArchived ? (
            <p className="bp5-running-text">
              <b>{fullName}</b> will need to be re-enrolled into a program to
              restart any outbound outreach. Are you sure you want to continue?
            </p>
          ) : (
            <React.Fragment>
              <p className="bp5-running-text">
                Are you sure? Archiving <b>{fullName}</b> will end all
                associated active program schedules, which will go into effect 1
                day after the archive is completed. <b>{fullName}</b> will no
                longer receive any communications and will no longer show up on
                your dashboard.
                <br />
                <br />
                However, you can always unarchive <b>{fullName}</b> by searching
                for the {terms.patient.default} via the search bar, though the
                result will be hidden by default unless you select "Show
                Archived {terms.patient.capitalized}". If unarchived,{' '}
                <b>{fullName}</b> will need to be re-enrolled into a program to
                restart any outbound outreach. {terms.patient.capitalized} data
                will NOT be lost after archiving.
              </p>
              {numArchiveOptions > 5 ? (
                <Label className="pt-7">
                  Archive Reason:
                  <SimpleSelect
                    options={archiveOptions}
                    onChange={handleReasonSelection}
                    value={archiveReason}
                  />
                </Label>
              ) : (
                <RadioGroup
                  label="Archive Reason:"
                  className="pt-7"
                  options={archiveOptions}
                  onChange={handleReasonSelection}
                  selectedValue={archiveReason}
                />
              )}
              {archiveReason === 'other' && (
                <Label className="pt-2">
                  Comment (Optional):
                  <TextArea
                    fill
                    onChange={handleCommentChange}
                    value={archiveComment}
                  />
                </Label>
              )}
            </React.Fragment>
          )}
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              intent="primary"
              onClick={handleConfirm}
              disabled={isConfirmDisabled(
                patient.isArchived,
                archiveReason.length > 0
              )}
            >
              Confirm
            </Button>
          </div>
        </div>
      </Dialog>
      <AnchorButton
        className={cc(classNames)}
        onClick={() => setIsDialogOpen(true)}
        bare={bare}
        fill={fill}
        variant="minimal"
      >
        {compact ? (
          <Tooltip content={title}>
            <Icon icon={icon} title={title} />
          </Tooltip>
        ) : (
          <>
            <Icon className="mr-2" icon={icon} title={title} />
            <Text dotted>
              <span role="status">{title}</span>
            </Text>
          </>
        )}
      </AnchorButton>
    </React.Fragment>
  );
};
